// src/admin/components/owners/OwnerList.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiUserLine,
  RiAddLine,
  RiFilter2Line,
  RiSearchLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiEyeOffLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiRefreshLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiMoneyDollarCircleLine,
  RiBuildingLine,
  RiLayoutGridLine,
  RiListUnordered,
  RiUserHeartLine,
  RiWallet3Line,
  RiHistoryLine,
  RiNotificationLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiMoreLine,
  RiSortAsc,
  RiSortDesc,
  RiFileExcelLine,
  RiFilePdfLine,
  RiPrinterLine,
  RiSettings3Line,
  RiDashboardLine,
  RiHome4Line,
  RiUserSettingsLine,
  RiBuildingFill,
  RiBarChartBoxLine
} from 'react-icons/ri';
import {
  getAllOwners,
  deleteOwner,
  formatOwnerDisplayName,
  formatCurrency,
  formatPhoneNumber,
  getOwnerStatusColor,
  COUNTRY_OPTIONS,
  getOwnerProperties
} from '../../../models/ownerModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser } from '../../../models/authModel';

const OwnerList = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedOwners, setExpandedOwners] = useState({});
  const [ownerApartments, setOwnerApartments] = useState({});
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadingApartments, setLoadingApartments] = useState({});

  const [filters, setFilters] = useState({
    livingThere: '',
    searchTerm: '',
    minApartments: '',
    maxApartments: '',
    apartmentStatus: '',
    occupancyRate: '',
    country: ''
  });

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    ownerId: null,
    ownerName: ''
  });

  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [moreActionsOpen, setMoreActionsOpen] = useState(null);

  // Fetch owners data
  const fetchOwners = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters = {
        page,
        limit: itemsPerPage
      };

      if (filters.livingThere !== '') {
        apiFilters.livingThere = filters.livingThere === 'true';
      }

      if (filters.minApartments) {
        apiFilters.minApartments = parseInt(filters.minApartments);
      }

      if (filters.maxApartments) {
        apiFilters.maxApartments = parseInt(filters.maxApartments);
      }

      if (filters.apartmentStatus) {
        apiFilters.apartmentStatus = filters.apartmentStatus;
      }

      if (filters.occupancyRate) {
        apiFilters.occupancyRate = parseInt(filters.occupancyRate);
      }

      if (filters.country) {
        apiFilters.country = filters.country;
      }

      // Add sort parameters
      apiFilters.sortBy = sortField;
      apiFilters.sortDirection = sortDirection;

      const response = await getAllOwners(apiFilters);

      if (response && response.owners) {
        setOwners(response.owners);
        setTotalItems(response.total || response.owners.length);

        // Reset selected owners when fetching new data
        setSelectedOwners([]);
        setSelectAll(false);
      } else {
        setOwners([]);
        setTotalItems(0);
      }

    } catch (err) {
      console.error('Error fetching owners:', err);

      let errorMessage = t('failed_to_load_owners_data') || 'Failed to load owners data';

      if (err.response?.status === 401) {
        errorMessage = t('authentication_failed') || 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = t('access_denied') || 'Access denied. Admin role required.';
      } else if (err.response?.status === 500) {
        errorMessage = t('server_error') || 'Server error. Please check if the backend is running.';
      } else if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
        errorMessage = t('connection_error') || 'Cannot connect to server. Please check if backend is running.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwners(1); // Reset to first page when filters change
    setCurrentPage(1);
  }, [filters.livingThere, filters.minApartments, filters.maxApartments, filters.apartmentStatus, filters.occupancyRate, filters.country, sortField, sortDirection, itemsPerPage]);

  // Fetch owner's apartments when expanded
  const fetchOwnerApartments = async (ownerId) => {
    if (ownerApartments[ownerId]) return; // Already fetched

    try {
      setLoadingApartments(prev => ({ ...prev, [ownerId]: true }));
      const response = await getOwnerProperties(ownerId);

      if (response && response.apartments) {
        setOwnerApartments(prev => ({
          ...prev,
          [ownerId]: response.apartments
        }));
      }
    } catch (err) {
      console.error(`Error fetching apartments for owner ${ownerId}:`, err);
    } finally {
      setLoadingApartments(prev => ({ ...prev, [ownerId]: false }));
    }
  };

  // Toggle owner expansion to show apartments
  const toggleOwnerExpansion = (ownerId) => {
    setExpandedOwners(prev => {
      const isCurrentlyExpanded = prev[ownerId];

      // If expanding, fetch apartments if not already loaded
      if (!isCurrentlyExpanded && !ownerApartments[ownerId]) {
        fetchOwnerApartments(ownerId);
      }

      return {
        ...prev,
        [ownerId]: !isCurrentlyExpanded
      };
    });
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchOwners(currentPage);
  };

  // Handle search
  const handleSearch = (e) => {
    setFilters({ ...filters, searchTerm: e.target.value });
  };

  // Apply search filter
  const handleApplySearch = () => {
    fetchOwners(1); // Reset to first page when searching
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      livingThere: '',
      searchTerm: '',
      minApartments: '',
      maxApartments: '',
      apartmentStatus: '',
      occupancyRate: '',
      country: ''
    });
  };

  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Filter owners based on search term (client-side filtering)
  const filteredOwners = owners.filter(owner => {
    if (!filters.searchTerm) return true;

    const searchTerm = filters.searchTerm.toLowerCase();
    return (
      owner.name.toLowerCase().includes(searchTerm) ||
      owner.phone.toLowerCase().includes(searchTerm)
    );
  });

  // Handle sort
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle delete confirmation dialog
  const openDeleteDialog = (ownerId, ownerName) => {
    setDeleteDialog({
      isOpen: true,
      ownerId,
      ownerName
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      ownerId: null,
      ownerName: ''
    });
  };

  // Handle owner deletion
  const handleDeleteOwner = async () => {
    if (!deleteDialog.ownerId) return;

    try {
      setDeleting(true);

      await deleteOwner(deleteDialog.ownerId);

      // Refresh the owner list
      fetchOwners(currentPage);

      // Close dialog
      closeDeleteDialog();

    } catch (err) {
      console.error('Error deleting owner:', err);
      setError(err.message || t('failed_to_delete_owner') || 'Failed to delete owner');
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedOwners.length === 0) return;

    const confirmDelete = window.confirm(
      t('confirm_bulk_delete', { count: selectedOwners.length }) ||
      `Are you sure you want to delete ${selectedOwners.length} owners? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      // In a real implementation, you would have a bulk delete API endpoint
      // For now, we'll delete them one by one
      for (const ownerId of selectedOwners) {
        await deleteOwner(ownerId);
      }

      // Refresh the owner list
      fetchOwners(currentPage);

      // Clear selection
      setSelectedOwners([]);
      setSelectAll(false);

    } catch (err) {
      console.error('Error with bulk delete:', err);
      setError(err.message || t('failed_to_delete_owners') || 'Failed to delete owners');
    } finally {
      setLoading(false);
    }
  };

  // Handle check/uncheck all
  const handleSelectAll = () => {
    if (selectAll) {
      // Unselect all
      setSelectedOwners([]);
    } else {
      // Select all
      setSelectedOwners(owners.map(owner => owner.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectOwner = (ownerId) => {
    setSelectedOwners(prev => {
      if (prev.includes(ownerId)) {
        // Remove from selection
        const newSelection = prev.filter(id => id !== ownerId);
        setSelectAll(newSelection.length === owners.length);
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, ownerId];
        setSelectAll(newSelection.length === owners.length);
        return newSelection;
      }
    });
  };

  // Handle export (placeholder)
  const handleExport = (format) => {
    setIsExporting(true);

    // Simulate export delay
    setTimeout(() => {
      console.log(`Exporting ${selectedOwners.length || 'all'} owners to ${format}`);
      setIsExporting(false);
      setMoreActionsOpen(null);
    }, 1500);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchOwners(page);
  };

  // Toggle more actions menu
  const toggleMoreActions = (ownerId) => {
    setMoreActionsOpen(prev => prev === ownerId ? null : ownerId);
  };

  // Calculate owner summary statistics
  const calculateOwnerSummary = (owner) => {
    const totalProperties = owner.total_apartments || 0;
    const occupiedProperties = owner.occupied_apartments || 0;
    const vacantProperties = owner.vacant_apartments || 0;

    // Calculate occupancy rate
    const occupancyRate = totalProperties > 0
      ? Math.round((occupiedProperties / totalProperties) * 100)
      : 0;

    return {
      totalProperties,
      occupiedProperties,
      vacantProperties,
      occupancyRate
    };
  };

  // Count owners by status
  const livingThereCount = owners.filter(o => o.livingThere).length;
  const notLivingThereCount = owners.filter(o => !o.livingThere).length;
  const ownersWithPropertiesCount = owners.filter(o => o.total_apartments > 0).length;
  const ownersWithoutPropertiesCount = owners.filter(o => o.total_apartments === 0).length;

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Grid View Component
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredOwners.map((owner) => {
        const summary = calculateOwnerSummary(owner);
        const isExpanded = expandedOwners[owner.id] || false;
        const apartments = ownerApartments[owner.id] || [];
        const isLoading = loadingApartments[owner.id] || false;

        return (
          <div
            key={owner.id}
            className={`${isDarkMode ? 'bg-zinc-700 border-zinc-600' : 'bg-white border-zinc-200'} border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden`}
          >
            <div className="relative">
              {/* Selection Checkbox */}
              <div className="absolute top-4 left-4 z-10">
                <input
                  type="checkbox"
                  checked={selectedOwners.includes(owner.id)}
                  onChange={() => handleSelectOwner(owner.id)}
                  className={`h-5 w-5 rounded ${isDarkMode ? 'bg-zinc-600 border-zinc-500' : 'bg-white border-zinc-300'
                    } text-red-600 focus:ring-red-500`}
                />
              </div>

              {/* Owner Header */}
              <div className={`p-4 pl-12 ${isDarkMode ? 'border-b border-zinc-600' : 'border-b border-zinc-200'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md mr-3">
                      <span className="font-bold text-white text-sm">
                        {owner.name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        {formatOwnerDisplayName(owner)}
                      </h3>
                      <div className="flex items-center text-sm mt-1">
                        <RiPhoneLine className={`mr-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`} size={14} />
                        <span className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                          {formatPhoneNumber(owner.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOwnerStatusColor(owner.livingThere, isDarkMode)}`}>
                      {owner.livingThere ? t('resident_owner') || 'Resident Owner' : t('owner') || 'Owner'}
                    </span>

                    {/* More actions dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => toggleMoreActions(owner.id)}
                        className={`p-1 rounded ${isDarkMode ? 'hover:bg-zinc-600' : 'hover:bg-zinc-100'
                          }`}
                      >
                        <RiMoreLine size={18} className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`} />
                      </button>

                      {moreActionsOpen === owner.id && (
                        <div className={`absolute end-0 mt-1 w-48 rounded-md shadow-lg z-20 ${isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-zinc-200'
                          }`}>
                          <div className="py-1">
                            <Link
                              to={`/admin/owners/${owner.id}/edit`}
                              className={`flex items-center px-4 py-2 text-sm ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                                }`}
                            >
                              <RiPencilLine className="mr-2" size={16} />
                              {t('edit_owner') || 'Edit Owner'}
                            </Link>
                            <Link
                              to={`/admin/owners/${owner.id}/properties`}
                              className={`flex items-center px-4 py-2 text-sm ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                                }`}
                            >
                              <RiBuildingLine className="mr-2" size={16} />
                              {t('view_properties') || 'View Properties'}
                            </Link>
                            <button
                              onClick={() => openDeleteDialog(owner.id, formatOwnerDisplayName(owner))}
                              className={`flex items-center px-4 py-2 text-sm w-full text-left ${isDarkMode ? 'text-red-400 hover:bg-zinc-700' : 'text-red-600 hover:bg-zinc-100'
                                }`}
                            >
                              <RiDeleteBin6Line className="mr-2" size={16} />
                              {t('delete_owner') || 'Delete Owner'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Owner Details */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('apartments') || 'Apartments'}</p>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                      {summary.totalProperties}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('occupied') || 'Occupied'}</p>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                      {summary.occupiedProperties}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('vacant') || 'Vacant'}</p>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                      {summary.vacantProperties}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('occupancy_rate') || 'Occupancy Rate'}</p>
                    <p className={`text-sm font-semibold ${summary.occupancyRate > 80
                      ? isDarkMode ? 'text-green-400' : 'text-green-600'
                      : summary.occupancyRate > 50
                        ? isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
                        : isDarkMode ? 'text-red-400' : 'text-red-600'
                      }`}>
                      {summary.occupancyRate}%
                    </p>
                  </div>
                </div>

                {/* Occupancy Rate Progress Bar */}
                <div className="w-full h-2 bg-zinc-200 rounded-full overflow-hidden mb-4">
                  <div
                    className={`h-full rounded-full ${summary.occupancyRate > 80
                      ? 'bg-green-500'
                      : summary.occupancyRate > 50
                        ? 'bg-yellow-500'
                        : 'bg-zinc-500'
                      }`}
                    style={{ width: `${summary.occupancyRate}%` }}
                  ></div>
                </div>

                {/* Show apartments toggle button */}
                {summary.totalProperties > 0 && (
                  <button
                    onClick={() => toggleOwnerExpansion(owner.id)}
                    className={`w-full flex items-center justify-center py-1.5 px-3 text-sm rounded ${isDarkMode
                      ? 'bg-zinc-600 text-zinc-300 hover:bg-zinc-500'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                  >
                    {isExpanded ? (
                      <>
                        <RiArrowUpSLine className="mr-1" />
                        {t('hide_apartments') || 'Hide Apartments'}
                      </>
                    ) : (
                      <>
                        <RiArrowDownSLine className="mr-1" />
                        {t('show_apartments') || 'Show Apartments'}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Expanded Apartments Section */}
              {isExpanded && (
                <div className={`px-4 pb-4 ${isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'}`}>
                  <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('apartments_count', { count: apartments.length }) || `Apartments (${apartments.length})`}
                  </h4>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${isDarkMode ? 'border-red-400 border-t-transparent' : 'border-red-500 border-t-transparent'
                        }`}></div>
                      <span className={`ml-2 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        {t('loading_apartments') || 'Loading apartments...'}
                      </span>
                    </div>
                  ) : apartments.length === 0 ? (
                    <div className={`text-center py-3 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      {t('no_apartments_found') || 'No apartments found'}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                      {apartments.map(apartment => (
                        <div
                          key={apartment.id}
                          className={`p-2 rounded ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-650' : 'bg-white hover:bg-zinc-50'
                            } border ${isDarkMode ? 'border-zinc-600' : 'border-zinc-200'
                            }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                {apartment.apartmentNumber}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                {apartment.buildingName} • {t('floor') || 'Floor'} {apartment.floor}
                              </div>
                            </div>
                            <div>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${apartment.status === (t('status_vacant') || 'vacant')
                                ? (isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800')
                                : apartment.status === (t('status_occupied') || 'occupied')
                                  ? (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-800')
                                  : (isDarkMode ? 'bg-yellow-900 bg-opacity-20 text-yellow-400' : 'bg-yellow-100 text-yellow-800')
                                }`}>
                                {t(apartment.status) || apartment.status.charAt(0).toUpperCase() + apartment.status.slice(1)}
                              </span>
                            </div>
                          </div>

                          <div className="flex justify-between items-center mt-1">
                            <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                              {t(apartment.type) || apartment.type} • {apartment.size} {t('size_unit_sqm') || 'm²'}
                            </div>
                            <Link
                              to={`/admin/apartments/${apartment.id}`}
                              className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                            >
                              {t('view') || 'View'}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Owner Actions */}
              <div className={`px-4 py-3 ${isDarkMode ? 'bg-zinc-800 border-t border-zinc-600' : 'bg-zinc-50 border-t border-zinc-200'}`}>
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/owners/${owner.id}`}
                    className={`p-1.5 rounded ${isDarkMode ? 'text-green-400 hover:bg-zinc-700' : 'text-green-600 hover:bg-green-50'
                      }`}
                    title={t('view_properties') || "View properties"}
                  >
                    <RiBuildingLine size={16} />
                  </Link>

                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/admin/owners/${owner.id}`}
                      className={`p-1.5 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-700' : 'text-red-600 hover:bg-zinc-50'
                        }`}
                      title={t('view_details') || "View details"}
                    >
                      <RiUserHeartLine size={16} />
                    </Link>

                    <Link
                      to={`/admin/owners/${owner.id}/edit`}
                      className={`p-1.5 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-700' : 'text-red-600 hover:bg-zinc-50'
                        }`}
                      title={t('edit_owner') || "Edit owner"}
                    >
                      <RiPencilLine size={16} />
                    </Link>

                    {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk' || getCurrentUser()?.role === 'sales') && (
                      <button
                        onClick={() => openDeleteDialog(owner.id, formatOwnerDisplayName(owner))}
                        className={`p-1.5 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-700' : 'text-red-600 hover:bg-zinc-50'
                          }`}
                        title={t('delete_owner') || "Delete owner"}
                      >
                        <RiDeleteBin6Line size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // List View Component
  const ListView = () => (
    <div className="overflow-x-auto">
      <table className={`min-w-full divide-y ${isDarkMode ? 'divide-zinc-700' : 'divide-zinc-200'}`}>
        <thead className={isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'}>
          <tr>
            <th scope="col" className="px-3 py-3 text-left">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className={`h-4 w-4 rounded ${isDarkMode ? 'bg-zinc-700 border-zinc-600' : 'bg-white border-zinc-300'
                  } text-red-600 focus:ring-red-500`}
              />
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider cursor-pointer`}
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center">
                {t('owner') || 'Owner'}
                {sortField === 'name' && (
                  sortDirection === 'asc' ?
                    <RiSortAsc className="ml-1" /> :
                    <RiSortDesc className="ml-1" />
                )}
              </div>
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider cursor-pointer`}
              onClick={() => handleSort('livingThere')}
            >
              <div className="flex items-center">
                {t('status') || 'Status'}
                {sortField === 'livingThere' && (
                  sortDirection === 'asc' ?
                    <RiSortAsc className="ml-1" /> :
                    <RiSortDesc className="ml-1" />
                )}
              </div>
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider cursor-pointer`}
              onClick={() => handleSort('total_apartments')}
            >
              <div className="flex items-center">
                {t('apartments') || 'Apartments'}
                {sortField === 'total_apartments' && (
                  sortDirection === 'asc' ?
                    <RiSortAsc className="ml-1" /> :
                    <RiSortDesc className="ml-1" />
                )}
              </div>
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider`}
            >
              {t('occupancy') || 'Occupancy'}
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider`}
            >
              {t('contact') || 'Contact'}
            </th>
            <th
              scope="col"
              className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
                } uppercase tracking-wider`}
            >
              {t('actions') || 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className={`${isDarkMode ? 'bg-zinc-700 divide-y divide-zinc-600' : 'bg-white divide-y divide-zinc-200'}`}>
          {filteredOwners.map((owner) => {
            const summary = calculateOwnerSummary(owner);
            const isExpanded = expandedOwners[owner.id] || false;
            const apartments = ownerApartments[owner.id] || [];
            const isLoading = loadingApartments[owner.id] || false;

            return (
              <React.Fragment key={owner.id}>
                <tr className={`${isDarkMode
                  ? 'hover:bg-zinc-650 transition-colors'
                  : 'hover:bg-zinc-50 transition-colors'
                  } ${isExpanded ? (isDarkMode ? 'bg-zinc-650' : 'bg-zinc-50') : ''}`}>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedOwners.includes(owner.id)}
                      onChange={() => handleSelectOwner(owner.id)}
                      className={`h-4 w-4 rounded ${isDarkMode ? 'bg-zinc-600 border-zinc-500' : 'bg-white border-zinc-300'
                        } text-red-600 focus:ring-red-500`}
                    />
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm mr-3">
                        <span className="font-bold text-white text-sm">
                          {owner.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                          {formatOwnerDisplayName(owner)}
                        </div>
                        {owner.livingThere && (
                          <div className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {t('lives_on_property') || 'Lives on property'}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOwnerStatusColor(owner.livingThere, isDarkMode)}`}>
                      {owner.livingThere ? t('resident_owner') || 'Resident Owner' : t('owner') || 'Owner'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleOwnerExpansion(owner.id)}
                      className="flex items-center space-x-1 focus:outline-none"
                    >
                      <span className={`${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        {summary.totalProperties}
                      </span>
                      <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        ({summary.occupiedProperties} {t('occupied_lowercase') || 'occupied'})
                      </span>
                      {summary.totalProperties > 0 && (
                        isExpanded ? (
                          <RiArrowUpSLine className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'} />
                        ) : (
                          <RiArrowDownSLine className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'} />
                        )
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-full h-2 bg-zinc-200 rounded-full overflow-hidden mr-2 max-w-[100px]">
                        <div
                          className={`h-full rounded-full ${summary.occupancyRate > 80
                            ? 'bg-green-500'
                            : summary.occupancyRate > 50
                              ? 'bg-yellow-500'
                              : 'bg-zinc-500'
                            }`}
                          style={{ width: `${summary.occupancyRate}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${summary.occupancyRate > 80
                        ? isDarkMode ? 'text-green-400' : 'text-green-600'
                        : summary.occupancyRate > 50
                          ? isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
                          : isDarkMode ? 'text-red-400' : 'text-red-600'
                        }`}>
                        {summary.occupancyRate}%
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm">
                      <RiPhoneLine className={`mr-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`} size={16} />
                      <span className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                        {formatPhoneNumber(owner.phone)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <Link
                        to={`/admin/owners/${owner.id}`}
                        className={`p-1.5 rounded ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-600' : 'text-zinc-600 hover:bg-zinc-50'
                          }`}
                        title={t('view_details') || "View details"}
                      >
                        <RiUserHeartLine size={16} />
                      </Link>

                      <Link
                        to={`/admin/owners/${owner.id}/edit`}
                        className={`p-1.5 rounded ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-600' : 'text-zinc-600 hover:bg-zinc-50'
                          }`}
                        title={t('edit_owner') || "Edit owner"}
                      >
                        <RiPencilLine size={16} />
                      </Link>

                      {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk' || getCurrentUser()?.role === 'sales') && (
                        <button
                          onClick={() => openDeleteDialog(owner.id, formatOwnerDisplayName(owner))}
                          className={`p-1.5 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-600' : 'text-red-600 hover:bg-zinc-50'
                            }`}
                          title={t('delete_owner') || "Delete owner"}
                        >
                          <RiDeleteBin6Line size={16} />
                        </button>
                      )}

                      {/* More actions dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => toggleMoreActions(owner.id)}
                          className={`p-1.5 rounded ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-600' : 'text-zinc-600 hover:bg-zinc-100'
                            }`}
                          title={t('more_actions') || "More actions"}
                        >
                          <RiMoreLine size={16} />
                        </button>

                        {moreActionsOpen === owner.id && (
                          <div className={`absolute end-0 mt-1 w-48 rounded-md shadow-lg z-20 ${isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-zinc-200'
                            }`}>
                            <div className="py-1">
                              <Link
                                to={`/admin/owners/${owner.id}`}
                                className={`flex items-center px-4 py-2 text-sm ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                                  }`}
                              >
                                <RiBuildingLine className="mr-2" size={16} />
                                {t('view_properties') || 'View Properties'}
                              </Link>
                              <Link
                                to={`/admin/owners/${owner.id}`}
                                className={`flex items-center px-4 py-2 text-sm ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                                  }`}
                              >
                                <RiWallet3Line className="mr-2" size={16} />
                                {t('view_payments') || 'View Payments'}
                              </Link>
                              <button
                                onClick={() => {
                                  alert(t('feature_not_implemented') || 'This feature is not implemented yet');
                                  setMoreActionsOpen(null);
                                }}
                                className={`flex items-center px-4 py-2 text-sm w-full text-left ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                                  }`}
                              >
                                <RiNotificationLine className="mr-2" size={16} />
                                {t('send_reminder') || 'Send Reminder'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Expanded Apartments Row */}
                {isExpanded && (
                  <tr className={isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'}>
                    <td colSpan="7" className="px-4 py-4">
                      <div className="ml-12">
                        <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                          {t('apartments_count', { count: apartments.length }) || `Apartments (${apartments.length})`}
                        </h4>

                        {isLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${isDarkMode ? 'border-red-400 border-t-transparent' : 'border-red-500 border-t-transparent'
                              }`}></div>
                            <span className={`ml-2 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                              {t('loading_apartments') || 'Loading apartments...'}
                            </span>
                          </div>
                        ) : apartments.length === 0 ? (
                          <div className={`text-center py-3 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                            {t('no_apartments_found') || 'No apartments found'}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto pr-2">
                            {apartments.map(apartment => (
                              <div
                                key={apartment.id}
                                className={`p-2 rounded ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-650' : 'bg-white hover:bg-zinc-50'
                                  } border ${isDarkMode ? 'border-zinc-600' : 'border-zinc-200'
                                  }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                      {apartment.apartmentNumber}
                                    </div>
                                    <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                      {apartment.buildingName} • {t('floor') || 'Floor'} {apartment.floor}
                                    </div>
                                  </div>
                                  <div>
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${apartment.status === (t('status_vacant') || 'vacant')
                                      ? (isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800')
                                      : apartment.status === (t('status_occupied') || 'occupied')
                                        ? (isDarkMode ? 'bg-zinc-900 bg-opacity-20 text-zinc-400' : 'bg-zinc-100 text-zinc-800')
                                        : (isDarkMode ? 'bg-yellow-900 bg-opacity-20 text-yellow-400' : 'bg-yellow-100 text-yellow-800')
                                      }`}>
                                      {t(apartment.status) || apartment.status.charAt(0).toUpperCase() + apartment.status.slice(1)}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex justify-between items-center mt-1">
                                  <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                    {t(apartment.type) || apartment.type} • {apartment.size} {t('size_unit_sqm') || 'm²'}
                                  </div>
                                  <Link
                                    to={`/admin/apartments/${apartment.id}`}
                                    className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                                  >
                                    {t('view') || 'View'}
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">

      <div className={`${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-md overflow-hidden`}>

        {/* Delete Confirmation Dialog */}
        {deleteDialog.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className={`${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-lg max-w-md w-full p-6`}>
              <div className="flex items-center mb-4 text-red-500">
                <RiAlertLine size={24} className="mr-2" />
                <h3 className="text-lg font-semibold">{t('confirm_deletion') || 'Confirm Deletion'}</h3>
              </div>

              <p className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'} mb-6`}>
                {t('delete_owner_confirmation', { name: deleteDialog.ownerName }) || `Are you sure you want to delete owner "${deleteDialog.ownerName}"? This action cannot be undone.`}
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteDialog}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
                    }`}
                  disabled={deleting}
                >
                  {t('cancel') || 'Cancel'}
                </button>

                <button
                  onClick={handleDeleteOwner}
                  className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center ${deleting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('deleting') || 'Deleting...'}
                    </>
                  ) : (
                    <>
                      <RiDeleteBin6Line className="mr-2" />
                      {t('delete') || 'Delete'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={`p-4 ${isDarkMode ? 'border-b border-zinc-700' : 'border-b border-zinc-200'}`}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center space-x-4">
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'} flex items-center`}>
                <RiUserLine className="mr-2 text-red-500" />
                {t('property_owners') || 'Property Owners'}
              </h2>

              <button
                onClick={handleRefresh}
                className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  } transition-colors duration-200`}
                title={t('refresh_owners') || "Refresh owners"}
              >
                <RiRefreshLine size={18} />
              </button>

              {/* Bulk Actions Menu */}
              {selectedOwners.length > 0 && (
                <div className={`px-3 py-2 rounded-lg ${isDarkMode ? 'bg-red-600' : 'bg-zinc-500'} text-white`}>
                  <span className="mr-2 text-sm">{selectedOwners.length} {t('selected') || 'selected'}</span>
                  <button
                    onClick={handleBulkDelete}
                    className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'
                      } text-white ml-2 text-xs`}
                    title={t('delete_selected_owners') || "Delete selected owners"}
                  >
                    <RiDeleteBin6Line className="mr-1 inline-block" size={14} />
                    {t('delete') || 'Delete'}
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="relative w-full sm:w-64">
                <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none text-zinc-400">
                  <RiSearchLine size={18} />
                </div>
                <input
                  type="search"
                  className={`block w-full p-2 ps-10 text-sm ${isDarkMode
                    ? 'bg-zinc-700 border-zinc-600 text-white'
                    : 'bg-zinc-50 border-zinc-300 text-zinc-900'
                    } border rounded-lg focus:ring-red-500 focus:border-red-500`}
                  placeholder={t('search_owners') || 'Search owners...'}
                  value={filters.searchTerm}
                  onChange={handleSearch}
                  onKeyPress={e => e.key === 'Enter' && handleApplySearch()}
                />
              </div>

              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border-zinc-300'
                  } border`}
              >
                <RiFilter2Line className="mr-2" />
                {t('filter') || 'Filter'}
                {Object.values(filters).some(val => val !== '' && val !== filters.searchTerm) && (
                  <span className={`ml-2 h-5 w-5 flex items-center justify-center rounded-full text-xs ${isDarkMode ? 'bg-red-600 text-white' : 'bg-red-600 text-white'
                    }`}>
                    {Object.values(filters).filter(val => val !== '' && val !== filters.searchTerm).length}
                  </span>
                )}
              </button>

              {/* View Mode Toggle */}
              <button
                onClick={toggleViewMode}
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border-zinc-300'
                  } border`}
                title={viewMode === 'grid' ? t('switch_to_list_view') || 'Switch to List View' : t('switch_to_grid_view') || 'Switch to Grid View'}
              >
                {viewMode === 'grid' ? (
                  <>
                    <RiListUnordered className="mr-2" />
                    {t('list_view') || 'List'}
                  </>
                ) : (
                  <>
                    <RiLayoutGridLine className="mr-2" />
                    {t('grid_view') || 'Grid'}
                  </>
                )}
              </button>

              {/* Export Button */}
              <div className="relative">
                <button
                  onClick={() => setMoreActionsOpen(moreActionsOpen === 'export' ? null : 'export')}
                  className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                    ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600'
                    : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border-zinc-300'
                    } border`}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('exporting') || 'Exporting...'}
                    </>
                  ) : (
                    <>
                      <RiFileExcelLine className="mr-2" />
                      {t('export') || 'Export'}
                      <RiArrowDownSLine className="ml-2" />
                    </>
                  )}
                </button>

                {moreActionsOpen === 'export' && (
                  <div className={`absolute end-0 mt-1 w-48 rounded-md shadow-lg z-20 ${isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-zinc-200'
                    }`}>
                    <div className="py-1">
                      <button
                        onClick={() => handleExport('excel')}
                        className={`flex items-center px-4 py-2 text-sm w-full text-left ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                          }`}
                      >
                        <RiFileExcelLine className="mr-2" size={16} />
                        {t('export_to_excel') || 'Export to Excel'}
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        className={`flex items-center px-4 py-2 text-sm w-full text-left ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                          }`}
                      >
                        <RiFilePdfLine className="mr-2" size={16} />
                        {t('export_to_pdf') || 'Export to PDF'}
                      </button>
                      <button
                        onClick={() => handleExport('print')}
                        className={`flex items-center px-4 py-2 text-sm w-full text-left ${isDarkMode ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                          }`}
                      >
                        <RiPrinterLine className="mr-2" size={16} />
                        {t('print_owners') || 'Print Owners'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/admin/owners/add"
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
                  } transition-colors duration-200`}
              >
                <RiAddLine className="mr-2" />
                {t('add_owner') || 'Add Owner'}
              </Link>
            </div>
          </div>

          {/* Filter dropdown */}
          {filterOpen && (
            <div className={`mt-4 p-4 ${isDarkMode ? 'bg-zinc-700' : 'bg-zinc-50'} rounded-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('living_status') || 'Living Status'}
                  </label>
                  <select
                    value={filters.livingThere}
                    onChange={(e) => setFilters({ ...filters, livingThere: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                  >
                    <option value="">{t('all') || 'All'}</option>
                    <option value="true">{t('living_on_property') || 'Living on Property'}</option>
                    <option value="false">{t('not_living_on_property') || 'Not Living on Property'}</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('country') || 'Country'}
                  </label>
                  <select
                    value={filters.country}
                    onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                  >
                    <option value="">{t('all_countries') || 'All Countries'}</option>
                    {COUNTRY_OPTIONS.map(country => (
                      <option key={country.value} value={country.value}>
                        {t(country.label) || country.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('apartment_status') || 'Apartment Status'}
                  </label>
                  <select
                    value={filters.apartmentStatus}
                    onChange={(e) => setFilters({ ...filters, apartmentStatus: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                  >
                    <option value="">{t('all_statuses') || 'All Statuses'}</option>
                    <option value="vacant">{t('with_vacant') || 'With Vacant Apartments'}</option>
                    <option value="occupied">{t('fully_occupied') || 'Fully Occupied'}</option>
                    <option value="maintenance">{t('with_maintenance') || 'With Apartments Under Maintenance'}</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('min_apartments') || 'Min. Apartments'}
                  </label>
                  <input
                    type="number"
                    value={filters.minApartments}
                    onChange={(e) => setFilters({ ...filters, minApartments: e.target.value })}
                    min="0"
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                    placeholder={t('min_zero') || "0"}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('max_apartments') || 'Max. Apartments'}
                  </label>
                  <input
                    type="number"
                    value={filters.maxApartments}
                    onChange={(e) => setFilters({ ...filters, maxApartments: e.target.value })}
                    min="0"
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                    placeholder={t('unlimited') || "Unlimited"}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    {t('min_occupancy_rate') || 'Min. Occupancy Rate (%)'}
                  </label>
                  <input
                    type="number"
                    value={filters.occupancyRate}
                    onChange={(e) => setFilters({ ...filters, occupancyRate: e.target.value })}
                    min="0"
                    max="100"
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-600 border-zinc-500 text-white'
                      : 'bg-white border-zinc-300 text-zinc-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                    placeholder={t('min_zero') || "0"}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4 space-x-3">
                <button
                  onClick={handleClearFilters}
                  className={`px-3 py-1.5 text-sm rounded-lg ${isDarkMode
                    ? 'bg-zinc-600 text-zinc-300 hover:bg-zinc-500'
                    : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
                    }`}
                >
                  {t('clear_filters') || 'Clear Filters'}
                </button>
                <button
                  onClick={() => setFilterOpen(false)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${isDarkMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                >
                  {t('apply_filters') || 'Apply Filters'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'} flex items-center`}>
              <div className={`p-3 rounded-lg mr-3 ${isDarkMode ? 'bg-red-600 bg-opacity-20' : 'bg-zinc-100'}`}>
                <RiUserLine className={`text-xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {t('total_owners') || 'Total Owners'}
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {totalItems}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-700' : 'bg-green-50'} flex items-center`}>
              <div className={`p-3 rounded-lg mr-3 ${isDarkMode ? 'bg-green-600 bg-opacity-20' : 'bg-green-100'}`}>
                <RiMapPinLine className={`text-xl ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {t('resident_owners') || 'Resident Owners'}
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {livingThereCount}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-700' : 'bg-purple-50'} flex items-center`}>
              <div className={`p-3 rounded-lg mr-3 ${isDarkMode ? 'bg-purple-600 bg-opacity-20' : 'bg-purple-100'}`}>
                <RiBuildingLine className={`text-xl ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {t('with_apartments') || 'With Apartments'}
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {ownersWithPropertiesCount}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-700' : 'bg-zinc-50'} flex items-center`}>
              <div className={`p-3 rounded-lg mr-3 ${isDarkMode ? 'bg-red-600 bg-opacity-20' : 'bg-zinc-100'}`}>
                <RiAlertLine className={`text-xl ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {t('without_apartments') || 'Without Apartments'}
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {ownersWithoutPropertiesCount}
                </p>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`inline-block h-8 w-8 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-500 border-t-transparent'
                }`}></div>
              <p className={`mt-4 text-lg font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                {t('loading_owners') || 'Loading owners...'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200'
              } flex flex-col items-center justify-center text-center`}>
              <RiAlertLine className="text-4xl mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('error_loading_owners') || 'Error Loading Owners'}</h3>
              <p className="mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'
                  } flex items-center`}
              >
                <RiRefreshLine className="mr-2" />
                {t('try_again') || 'Try Again'}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && owners.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`p-8 rounded-full ${isDarkMode ? 'bg-zinc-700' : 'bg-zinc-100'} mb-4`}>
                <RiUserLine className={`text-6xl ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
                {t('no_owners_found') || 'No Property Owners Found'}
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'} max-w-md text-center`}>
                {t('no_owners_message') || "There are no property owners in the system yet. Click the 'Add Owner' button to create your first owner."}
              </p>
              <Link
                to="/admin/owners/add"
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'
                  } flex items-center`}
              >
                <RiAddLine className="mr-2" />
                {t('create_first_owner') || 'Create First Owner'}
              </Link>
            </div>
          )}

          {/* Owners Display - Grid or List View */}
          {!loading && !error && filteredOwners.length > 0 && (
            <>
              {viewMode === 'grid' ? <GridView /> : <ListView />}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`flex items-center justify-between mt-6 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  <div className="flex items-center space-x-2">
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                      className={`p-1.5 text-sm rounded border ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-700'
                        }`}
                    >
                      <option value="10">10 / {t('page') || 'page'}</option>
                      <option value="25">25 / {t('page') || 'page'}</option>
                      <option value="50">50 / {t('page') || 'page'}</option>
                      <option value="100">100 / {t('page') || 'page'}</option>
                    </select>
                    <span className="text-sm">
                      {t('showing_range', { start: startItem, end: endItem, total: totalItems }) ||
                        `Showing ${startItem}-${endItem} of ${totalItems}`}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={`p-1.5 rounded ${isDarkMode
                        ? 'hover:bg-zinc-700 disabled:text-zinc-600'
                        : 'hover:bg-zinc-100 disabled:text-zinc-400'
                        }`}
                    >
                      <span className="sr-only">{t('first_page') || 'First Page'}</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-1.5 rounded ${isDarkMode
                        ? 'hover:bg-zinc-700 disabled:text-zinc-600'
                        : 'hover:bg-zinc-100 disabled:text-zinc-400'
                        }`}
                    >
                      <span className="sr-only">{t('previous_page') || 'Previous Page'}</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Show pages around current page
                      let pageNum;
                      if (totalPages <= 5) {
                        // Less than 5 pages, show all
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        // Near start
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        // Near end
                        pageNum = totalPages - 4 + i;
                      } else {
                        // Middle
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded ${currentPage === pageNum
                            ? isDarkMode
                              ? 'bg-red-600 text-white'
                              : 'bg-zinc-500 text-white'
                            : isDarkMode
                              ? 'hover:bg-zinc-700'
                              : 'hover:bg-zinc-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`p-1.5 rounded ${isDarkMode
                        ? 'hover:bg-zinc-700 disabled:text-zinc-600'
                        : 'hover:bg-zinc-100 disabled:text-zinc-400'
                        }`}
                    >
                      <span className="sr-only">{t('next_page') || 'Next Page'}</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`p-1.5 rounded ${isDarkMode
                        ? 'hover:bg-zinc-700 disabled:text-zinc-600'
                        : 'hover:bg-zinc-100 disabled:text-zinc-400'
                        }`}
                    >
                      <span className="sr-only">{t('last_page') || 'Last Page'}</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L15.586 11H4a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Filtered results info */}
          {!loading && !error && owners.length > 0 && filteredOwners.length !== owners.length && (
            <div className={`mt-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'} text-center`}>
              {t('showing_filtered_results', {
                showing: filteredOwners.length,
                total: owners.length
              }) || `Showing ${filteredOwners.length} of ${owners.length} owners`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerList;